import React, { createContext, useState, useEffect, useCallback } from "react";
import { refreshToken, logout } from "./api";
import { jwtDecode } from "jwt-decode";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🚪 Logout global (useCallback para manter referência estável)
  const clearAuth = useCallback(() => {
    try {
      if (user?.refresh) logout(user.refresh);
    } catch (err) {
      console.warn("Erro ao efetuar logout remoto:", err);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    setUser(null);
  }, [user?.refresh]);

  // ♻️ Renovação do token (também estável)
  const handleRefresh = useCallback(
    async (refreshParam) => {
      const refreshValue = refreshParam || user?.refresh;
      if (!refreshValue) return clearAuth();

      try {
        const data = await refreshToken(refreshValue);

        if (data.token) {
          localStorage.setItem("token", data.token);
          const decoded = jwtDecode(data.token);
          setUser({ token: data.token, refresh: refreshValue, ...decoded });
        } else {
          clearAuth();
        }
      } catch (error) {
        console.error("Erro ao renovar token:", error);
        clearAuth();
      } finally {
        setLoading(false);
      }
    },
    [user?.refresh, clearAuth]
  );

  // 🧩 Inicialização segura
  useEffect(() => {
    const token = localStorage.getItem("token");
    const refresh = localStorage.getItem("refresh_token");

    if (!token || !refresh) {
      setLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const now = Date.now() / 1000;

      if (decoded.exp > now) {
        setUser({ token, refresh, ...decoded });
        setLoading(false);
      } else {
        handleRefresh(refresh);
      }
    } catch (err) {
      console.error("Erro ao decodificar token:", err);
      clearAuth();
      setLoading(false);
    }
  }, [handleRefresh, clearAuth]);

  // 🔁 Atualiza token periodicamente
  useEffect(() => {
    if (!user?.refresh) return;
    const interval = setInterval(() => handleRefresh(), 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.refresh, handleRefresh]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="dashboard-container">
          <div className="user-card">
            <div className="spinner"></div>
            <p>Verificando sessão...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logoutUser: clearAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
